import React, { useState } from 'react';
import {
    FileText, Code, CheckCircle2, List, Briefcase, GraduationCap,
    Star, BookOpen, Heart, User, Zap, ClipboardList, Hash, Copy, Check,
    ChevronDown, ChevronRight, Info
} from 'lucide-react';

interface DocumentShortcodesProps {
    onBack?: () => void;
}

const DocumentShortcodes: React.FC<DocumentShortcodesProps> = () => {
    const [copiedTag, setCopiedTag] = useState<string | null>(null);
    const [openSection, setOpenSection] = useState<string | null>(null);

    const copyTag = (tag: string) => {
        navigator.clipboard.writeText(tag).catch(() => {});
        setCopiedTag(tag);
        setTimeout(() => setCopiedTag(null), 1800);
    };

    const toggleSection = (id: string) => {
        setOpenSection(prev => prev === id ? null : id);
    };

    // ─── DATA ────────────────────────────────────────────────────────────────

    const personalShortcodes = [
        { tag: '[name]',         desc: 'Volledige naam kandidaat (bijv. Jan Jansen)' },
        { tag: '[voornaam]',     desc: 'Voornaam (bijv. Jan)' },
        { tag: '[achternaam]',   desc: 'Achternaam (bijv. Jansen)' },
        { tag: '[email]',        desc: 'E-mailadres' },
        { tag: '[phone]',        desc: 'Telefoonnummer' },
        { tag: '[address]',      desc: 'Straat en huisnummer' },
        { tag: '[zipCode]',      desc: 'Postcode' },
        { tag: '[city]',         desc: 'Woonplaats' },
        { tag: '[country]',      desc: 'Land' },
        { tag: '[location]',     desc: 'Samengestelde locatie (stad + land)' },
        { tag: '[dateOfBirth]',  desc: 'Geboortedatum' },
        { tag: '[placeOfBirth]', desc: 'Geboorteplaats' },
        { tag: '[linkedin]',     desc: 'LinkedIn profiel URL' },
    ];

    const profileShortcodes = [
        { tag: '[role]',          desc: 'Hoofdfunctietitel / Job Title' },
        { tag: '[summary]',       desc: 'Persoonlijke samenvatting / profielschets' },
        { tag: '[motivation]',    desc: 'Motivatietekst voor specifieke vacature' },
        { tag: '[customText]',    desc: 'Vrije tekstblokken samengevoegd' },
        { tag: '[skills_string]', desc: 'Alle vaardigheden als kommalijst (bijv. "Python, React, SQL")' },
        { tag: '[hobbies_string]',desc: 'Alle hobby\'s als kommalijst' },
    ];

    const experienceArrays = [
        { tag: '[experience1_role]',        desc: 'Functietitel werkervaring 1 (verhoog cijfer voor volgende items)' },
        { tag: '[experience1_company]',     desc: 'Bedrijfsnaam werkervaring 1' },
        { tag: '[experience1_period]',      desc: 'Periode werkervaring 1  (bijv. 2020–2023)' },
        { tag: '[experience1_description]', desc: 'Taakomschrijving werkervaring 1' },
        { tag: '[experience2_role]',        desc: 'Functietitel werkervaring 2  (enzovoort tot ∞)' },
    ];

    const educationArrays = [
        { tag: '[education1_degree]', desc: 'Diploma / naam opleiding 1' },
        { tag: '[education1_school]', desc: 'School of instituut opleiding 1' },
        { tag: '[education1_period]', desc: 'Jaar of periode opleiding 1' },
        { tag: '[education2_degree]', desc: 'Diploma / naam opleiding 2  (enzovoort)' },
    ];

    const courseArrays = [
        { tag: '[course1]',          desc: 'Naam cursus / training item 1' },
        { tag: '[courseinstitute1]', desc: 'Instituut cursus 1' },
        { tag: '[courseyear1]',      desc: 'Jaar cursus 1' },
        { tag: '[course2]',          desc: 'Naam cursus 2  (enzovoort)' },
    ];

    const eisenArrays = [
        { tag: '[eis1]', desc: 'Tekst van functie-eis / must-have item 1' },
        { tag: '[eis2]', desc: 'Functie-eis item 2  (enzovoort)' },
    ];

    const wensenArrays = [
        { tag: '[wens1]', desc: 'Tekst van wens / nice-to-have item 1' },
        { tag: '[wens2]', desc: 'Wens item 2  (enzovoort)' },
    ];

    const skillArrays = [
        { tag: '[skill1]',  desc: 'Eerste vaardigheid (op volgorde)' },
        { tag: '[skill2]',  desc: 'Tweede vaardigheid' },
        { tag: '[skill3]',  desc: 'Derde vaardigheid  (enzovoort tot [skill10])' },
        { tag: '[hobby1]',  desc: 'Eerste hobby' },
        { tag: '[hobby2]',  desc: 'Tweede hobby  (enzovoort tot [hobby10])' },
    ];

    const loopBlocks = [
        {
            id: 'workHistory',
            title: 'Werkervaring',
            icon: <Briefcase size={14} />,
            color: 'blue',
            openTag: '[#workHistory]',
            closeTag: '[/workHistory]',
            alias: 'Alias: [#experience] … [/experience]',
            innerVars: [
                { tag: '[role]',        desc: 'Functietitel' },
                { tag: '[company]',     desc: 'Bedrijfsnaam' },
                { tag: '[period]',      desc: 'Periode (bijv. 2020–2023)' },
                { tag: '[description]', desc: 'Taakomschrijving' },
            ],
            example: `[#workHistory]\nFunctie:  [role]\nBedrijf:  [company]\nPeriode:  [period]\n[description]\n[/workHistory]`,
        },
        {
            id: 'education',
            title: 'Opleiding',
            icon: <GraduationCap size={14} />,
            color: 'emerald',
            openTag: '[#education]',
            closeTag: '[/education]',
            alias: 'Aliassen: [educationschool], [educationyear]',
            innerVars: [
                { tag: '[degree]', desc: 'Diploma / opleidingsnaam' },
                { tag: '[school]', desc: 'School of instituut' },
                { tag: '[year]',   desc: 'Jaar of periode' },
            ],
            example: `[#education]\nDiploma: [degree]\nSchool:  [school]\nJaar:    [year]\n[/education]`,
        },
        {
            id: 'course',
            title: 'Cursussen & Trainingen',
            icon: <BookOpen size={14} />,
            color: 'violet',
            openTag: '[#course]',
            closeTag: '[/course]',
            alias: '',
            innerVars: [
                { tag: '[coursetitel]',    desc: 'Naam van de cursus' },
                { tag: '[courseinstitute]',desc: 'Instituut / aanbieder' },
                { tag: '[courseyear]',     desc: 'Jaar van afsluiting' },
            ],
            example: `[#course]\nCursus:    [coursetitel]\nInstituut: [courseinstitute]\nJaar:      [courseyear]\n[/course]`,
        },
        {
            id: 'eis',
            title: 'Functie-eisen (must-haves)',
            icon: <CheckCircle2 size={14} />,
            color: 'rose',
            openTag: '[#eis]',
            closeTag: '[/eis]',
            alias: '',
            innerVars: [
                { tag: '[eistitel]', desc: 'Naam / label van de eis' },
                { tag: '[eistekst]', desc: 'Detail of toelichting' },
            ],
            example: `[#eis]\n[eistitel]: [eistekst]\n[/eis]`,
        },
        {
            id: 'wens',
            title: 'Wensen (nice-to-haves)',
            icon: <Star size={14} />,
            color: 'amber',
            openTag: '[#wens]',
            closeTag: '[/wens]',
            alias: '',
            innerVars: [
                { tag: '[wenstitel]', desc: 'Naam / label van de wens' },
                { tag: '[wenstekst]', desc: 'Detail of toelichting' },
            ],
            example: `[#wens]\n[wenstitel]: [wenstekst]\n[/wens]`,
        },
        {
            id: 'skills',
            title: 'Vaardigheden',
            icon: <Zap size={14} />,
            color: 'cyan',
            openTag: '[#skills]',
            closeTag: '[/skills]',
            alias: 'Alias: [#skill] … [/skill]',
            innerVars: [
                { tag: '[skilldetail]', desc: 'Volledige vaardigheid tekst' },
                { tag: '[name]',        desc: 'Naam vaardigheid (kort)' },
            ],
            example: `[#skills]\n• [skilldetail]\n[/skills]`,
        },
    ];

    const colorMap: Record<string, string> = {
        blue:   'bg-blue-50 border-blue-200 text-blue-700',
        emerald:'bg-emerald-50 border-emerald-200 text-emerald-700',
        violet: 'bg-violet-50 border-violet-200 text-violet-700',
        rose:   'bg-rose-50 border-rose-200 text-rose-700',
        amber:  'bg-amber-50 border-amber-200 text-amber-700',
        cyan:   'bg-cyan-50 border-cyan-200 text-cyan-700',
    };

    const customShortcodes = [
        {
            tag: '[Jouw Eigen Veld]',
            desc: 'Maak een vrij veld! De Document Builder vindt alle eigen tags in jouw Word-sjabloon automatisch en toont ze als invulveld. Gebuik spaties of hoofdletters vrij — bijv. [Certificering], [Verklaring], [Jarenlange Ervaring].',
        },
    ];

    // ─── HELPERS ─────────────────────────────────────────────────────────────

    const Tag = ({ tag }: { tag: string }) => (
        <button
            onClick={() => copyTag(tag)}
            title="Klikken om te kopiëren"
            className="inline-flex items-center gap-1 font-mono text-[11px] font-bold text-[#185ABD] bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-md hover:bg-blue-100 transition-colors group"
        >
            {tag}
            <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                {copiedTag === tag ? <Check size={10} className="text-emerald-500" /> : <Copy size={10} />}
            </span>
        </button>
    );

    const renderTable = (items: { tag: string; desc: string }[]) => (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm mb-6">
            <table className="w-full text-left border-collapse">
                <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500 uppercase tracking-widest">
                        <th className="py-3 px-5 font-bold w-2/5">Shortcode</th>
                        <th className="py-3 px-5 font-bold">Beschrijving</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {items.map((item, i) => (
                        <tr key={i} className="hover:bg-slate-50/50 transition-colors group">
                            <td className="py-3 px-5">
                                <Tag tag={item.tag} />
                            </td>
                            <td className="py-3 px-5 text-sm text-slate-600">{item.desc}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const SectionHeader = ({ icon, title, color = 'slate' }: { icon: React.ReactNode; title: string; color?: string }) => (
        <h2 className="text-base font-black text-slate-800 flex items-center gap-2 mb-4 mt-8 uppercase tracking-wide">
            <span className="text-slate-400">{icon}</span>
            {title}
        </h2>
    );

    // ─── RENDER ──────────────────────────────────────────────────────────────

    return (
        <div className="flex flex-col h-full bg-slate-50 overflow-y-auto custom-scrollbar p-8">
            <div className="max-w-5xl mx-auto w-full">

                {/* Header */}
                <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center shadow-sm">
                        <Code size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-black text-slate-900 tracking-tight">Shortcodes & Tags Handleiding</h1>
                        <p className="text-sm text-slate-500 mt-0.5">
                            Typ deze shortcodes exact over in je Word documenten (.docx) om kandidaatgegevens automatisch te koppelen.
                        </p>
                    </div>
                </div>

                {/* Tip banner */}
                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 mb-8 text-sm text-blue-900 leading-relaxed flex gap-3">
                    <Info size={18} className="shrink-0 mt-0.5 text-blue-500" />
                    <div>
                        <strong>Hoe werkt het?</strong> Upload een Word-sjabloon in <em>Templates (.docx)</em>. Typ exact de tags (bijv.{' '}
                        <code className="bg-blue-100 px-1.5 py-0.5 rounded font-mono text-xs">[voornaam]</code>) in je document op de gewenste plek.
                        De CV Builder scant het bestand, toont alle velden als editor en vult ze automatisch in bij het genereren van een document!
                    </div>
                </div>

                {/* ── 1. Personalia ── */}
                <SectionHeader icon={<User size={18} />} title="Kandidaat Persoonsgegevens" />
                {renderTable(personalShortcodes)}

                {/* ── 2. Profiel ── */}
                <SectionHeader icon={<ClipboardList size={18} />} title="Profiel & Match Elementen" />
                {renderTable(profileShortcodes)}

                {/* ── 3. Genummerde arrays ── */}
                <SectionHeader icon={<Hash size={18} />} title="Genummerde Items (1-op-1 plaatsing)" />
                <p className="text-xs text-slate-500 mb-4 -mt-2 px-1">
                    Gebruik genummerde shortcodes om exact te bepalen <em>waar</em> je het 1e, 2e of 3e item in je layout plaatst.
                    Verhoog het getal voor elk volgend item (bijv.{' '}
                    <code className="bg-slate-100 px-1 rounded font-mono">[experience2_role]</code> voor het tweede item).
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                    <div>
                        <h3 className="text-xs font-black text-slate-600 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                            <Briefcase size={12} /> Werkervaring
                        </h3>
                        {renderTable(experienceArrays)}
                    </div>
                    <div>
                        <h3 className="text-xs font-black text-slate-600 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                            <GraduationCap size={12} /> Opleiding
                        </h3>
                        {renderTable(educationArrays)}
                    </div>
                    <div>
                        <h3 className="text-xs font-black text-slate-600 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                            <BookOpen size={12} /> Cursussen
                        </h3>
                        {renderTable(courseArrays)}
                    </div>
                    <div>
                        <h3 className="text-xs font-black text-slate-600 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                            <Zap size={12} /> Vaardigheden & Hobby's
                        </h3>
                        {renderTable(skillArrays)}
                    </div>
                    <div>
                        <h3 className="text-xs font-black text-slate-600 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                            <CheckCircle2 size={12} /> Functie-eisen (must-haves)
                        </h3>
                        {renderTable(eisenArrays)}
                    </div>
                    <div>
                        <h3 className="text-xs font-black text-slate-600 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                            <Star size={12} /> Wensen (nice-to-haves)
                        </h3>
                        {renderTable(wensenArrays)}
                    </div>
                </div>

                {/* ── 4. Loop blokken ── */}
                <SectionHeader icon={<Code size={18} />} title="Lijsten & Herhaling (Loop Blokken)" />
                <div className="bg-slate-100/60 border border-slate-200 rounded-xl p-4 mb-6 text-xs text-slate-600 leading-relaxed">
                    Lijsten werken met zogenaamde <strong>blocks</strong>. Typ de start tag (bijv.{' '}
                    <code className="bg-white border border-slate-200 px-1.5 py-0.5 rounded font-mono">[#experience]</code>) aan het begin van een sectie en de end tag{' '}
                    <code className="bg-white border border-slate-200 px-1.5 py-0.5 rounded font-mono">[/experience]</code>{' '}
                    aan het einde. Word herhaalt de styling daartussen voor alles in de lijst!
                </div>

                <div className="space-y-4 mb-8">
                    {loopBlocks.map(block => {
                        const isOpen = openSection === block.id;
                        return (
                            <div key={block.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                {/* Header row */}
                                <button
                                    onClick={() => toggleSection(block.id)}
                                    className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors group"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-black ${colorMap[block.color]}`}>
                                            {block.icon}
                                            {block.title}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <code className="font-mono text-xs text-[#185ABD] bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-md font-bold">{block.openTag}</code>
                                            <span className="text-slate-300 text-xs">…</span>
                                            <code className="font-mono text-xs text-[#185ABD] bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-md font-bold">{block.closeTag}</code>
                                        </div>
                                        {block.alias && (
                                            <span className="hidden md:inline text-[10px] text-slate-400 font-medium italic">{block.alias}</span>
                                        )}
                                    </div>
                                    <span className="text-slate-400 group-hover:text-slate-600 transition-colors ml-4 shrink-0">
                                        {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                    </span>
                                </button>

                                {/* Expanded detail */}
                                {isOpen && (
                                    <div className="border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-0">
                                        {/* Inner variables table */}
                                        <div className="p-5 border-r border-slate-100">
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Interne variabelen</p>
                                            <div className="space-y-2">
                                                {block.innerVars.map((v, i) => (
                                                    <div key={i} className="flex items-start gap-3">
                                                        <Tag tag={v.tag} />
                                                        <span className="text-xs text-slate-500 leading-tight mt-0.5">{v.desc}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        {/* Code example */}
                                        <div className="p-5 bg-slate-50">
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Voorbeeld in Word</p>
                                            <pre className="text-[11px] font-mono leading-relaxed bg-slate-800 text-emerald-300 p-4 rounded-xl overflow-x-auto whitespace-pre">
                                                {block.example}
                                            </pre>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* ── 5. Eigen velden ── */}
                <SectionHeader icon={<Heart size={18} />} title="Dynamische & Eigen Vrije Velden" />
                {renderTable(customShortcodes)}

                {/* Quick copy cheatsheet */}
                <div className="mt-8 bg-slate-900 rounded-2xl p-6 text-slate-400 text-xs leading-loose font-mono">
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-4">⚡ Snelle Referentie — klik om te kopiëren</p>
                    <div className="flex flex-wrap gap-2">
                        {[
                            '[name]','[voornaam]','[achternaam]','[email]','[phone]','[city]','[role]','[summary]',
                            '[motivation]','[skills_string]',
                            '[experience1_role]','[experience1_company]','[experience1_period]','[experience1_description]',
                            '[education1_degree]','[education1_school]','[education1_period]',
                            '[course1]','[courseinstitute1]','[courseyear1]',
                            '[eis1]','[eis2]','[wens1]','[wens2]',
                            '[skill1]','[skill2]','[skill3]',
                            '[#workHistory]','[/workHistory]','[#education]','[/education]',
                            '[#course]','[/course]','[#eis]','[/eis]','[#wens]','[/wens]',
                            '[#skills]','[/skills]',
                        ].map(tag => (
                            <button
                                key={tag}
                                onClick={() => copyTag(tag)}
                                title="Kopiëren"
                                className={`px-2 py-1 rounded border text-[10px] transition-all ${
                                    copiedTag === tag
                                        ? 'bg-emerald-900/40 border-emerald-700 text-emerald-300'
                                        : 'border-slate-700 hover:border-blue-500 hover:text-blue-300'
                                }`}
                            >
                                {copiedTag === tag ? '✓ ' : ''}{tag}
                            </button>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default DocumentShortcodes;
